/**
 * A popover panel that renders to ``document.body`` and glues itself to
 * a trigger — the escape hatch for popovers that must open out of a
 * clipping, scrolling ancestor (the filter cajón). Opt-in: the filter
 * bar keeps its in-flow ``absolute`` popovers by default; only the
 * drawer path mounts this.
 *
 * Positioning is delegated to the pure ``computePopoverPosition`` so the
 * flip/clamp/height math is unit-tested away from the DOM (jsdom rects
 * are all zero). The React glue here — measure before paint, re-place on
 * scroll(capture)+resize, dismiss on outside click considering BOTH the
 * anchor and the panel, Escape in the capture phase — mirrors the proven
 * ``InfoTip`` pattern.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

import {
  computePopoverPosition,
  type PopoverPosition,
} from "./popover-position";

export interface PortalPopoverProps {
  /** The trigger the panel is anchored to. */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  /** Fired on outside click or Escape — the host owns the open state. */
  onClose: () => void;
  children: ReactNode;
  /**
   * Match the trigger's width (a dropdown). When false the panel keeps
   * its natural/content width. Default true.
   */
  matchAnchorWidth?: boolean;
  /** Horizontal alignment when there's room. Default "start". */
  align?: "start" | "center";
  /** Extra classes for the portalled panel. */
  className?: string;
  /** z-index for the panel. Default 1000 (above the drawer + backdrop). */
  zIndex?: number;
  /** Accessibility role for the panel (e.g. "listbox", "dialog"). */
  role?: string;
  ariaLabel?: string;
  id?: string;
}

export function PortalPopover({
  anchorRef,
  open,
  onClose,
  children,
  matchAnchorWidth = true,
  align = "start",
  className,
  zIndex = 1000,
  role,
  ariaLabel,
  id,
}: PortalPopoverProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PopoverPosition | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    // If the panel matches the anchor width, feed THAT width to the math
    // so the horizontal clamp is computed against the real footprint.
    const panelW = matchAnchorWidth ? a.width : p.width;
    setPos(
      computePopoverPosition(
        { left: a.left, top: a.top, width: a.width, height: a.height },
        { width: window.innerWidth, height: window.innerHeight },
        { panel: { width: panelW, height: p.height }, align },
      ),
    );
  }, [anchorRef, matchAnchorWidth, align]);

  // Measure before paint so the panel never flashes at (0,0), and keep
  // it glued while the page scrolls under it.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    place();
    const onMove = () => place();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, place]);

  // Outside click dismiss — a click inside the anchor OR the panel is
  // "inside" (the anchor click is what toggled it open; the panel is the
  // content). ``mousedown`` matches the in-flow pickers so the two
  // dismiss models stay consistent.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, anchorRef, onClose]);

  // Escape closes ONLY this popover — capture phase so a host modal's
  // bubble-phase Escape handler never fires (same fix as the date panel).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") return null;

  const anchorWidth = anchorRef.current?.getBoundingClientRect().width;

  return createPortal(
    <div
      data-dropdown-surface={role === "listbox" ? "list" : "filter"}
      ref={panelRef}
      id={id}
      role={role}
      aria-label={ariaLabel}
      className={className}
      style={{
        position: "fixed",
        left: pos ? pos.left : 0,
        top: pos ? pos.top : 0,
        // Hidden for the very first (pre-measure) commit so it never
        // paints at the origin; visible once ``place`` has run.
        visibility: pos ? "visible" : "hidden",
        zIndex,
        width: matchAnchorWidth && anchorWidth ? anchorWidth : undefined,
        maxHeight: pos ? pos.maxHeight : undefined,
        overflowY: "auto",
      }}
      data-placement={pos?.placement}
    >
      {children}
    </div>,
    document.body,
  );
}
